import { FC, useCallback, useRef, useState } from 'react';

import {
  GiftCouponForRedemption,
  GiftCouponForRedemptionDashboard,
  OrderCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Image } from '@shared/api/media/media.interface';

import {
  formatDate,
  formatDateTime,
  formatPhone,
  getChoiceUnitName,
  getImageUrl,
  getItemName,
  getOrdererInfo,
  getPriceInfo,
  isOverIssued,
} from '../utils/redemption.utils';

export interface VerificationData {
  verificationId: string;
  phoneNumber: string;
  code: string;
  verifiedAt: Date;
}

interface Props {
  filteredData: GiftCouponForRedemption[];
  selectedIds: Set<string>;
  deliveringId: string | null;
  productFilter: string;
  onToggleAll: () => void;
  onToggleSelect: (couponId: string) => void;
  onOpenDeliverModal: (couponId: string) => void;
  onOpenHistoryModal: (coupon: GiftCouponForRedemption) => void;
  onOpenShareHistoryModal: (coupon: GiftCouponForRedemption) => void;
  onOpenSignatureModal: (signatureImage: Image) => void;
  onOpenVerificationModal: (verification: VerificationData) => void;
  onUpdateRecipientPhone: (couponId: string, newPhone: string) => Promise<boolean>;
  isAllSelected: boolean;
  copyToClipboard: (text: string) => void;
  showToast: (msg: string) => void;
  /** 초과발송 강제 발송 모드 — true면 초과발송 건도 발송처리 버튼이 활성화됨 */
  allowOverIssue?: boolean;
}

export const RedemptionCouponTable: FC<Props> = ({
  filteredData,
  selectedIds,
  deliveringId,
  productFilter,
  onToggleAll,
  onToggleSelect,
  onOpenDeliverModal,
  onOpenHistoryModal,
  onOpenShareHistoryModal,
  onOpenSignatureModal,
  onOpenVerificationModal,
  onUpdateRecipientPhone,
  isAllSelected,
  copyToClipboard,
  showToast,
  allowOverIssue,
}) => {
  if (filteredData.length === 0) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <p className="text-gray-500">발송 대기 중인 쿠폰이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-12 px-4 py-3">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={onToggleAll}
                disabled={filteredData.length === 0}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
              />
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <div>쿠폰번호</div>
              <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                _id
              </div>
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              현재 발급상황
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              상품
            </th>
            <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              정가
            </th>
            <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              <div>판매가</div>
              <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                도매가
              </div>
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              주문자
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              구매일
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              메모
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <div className="flex items-center gap-1.5">
                수신자 정보
                {productFilter !== 'all' && (
                  <CopyPhonesButton
                    filteredData={filteredData}
                    copyToClipboard={copyToClipboard}
                    showToast={showToast}
                  />
                )}
              </div>
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <div>신청일시</div>
              <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                만료일
              </div>
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              액션
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {filteredData.map((coupon) => (
            <CouponRow
              key={coupon._id}
              coupon={coupon}
              isSelected={selectedIds.has(coupon._id)}
              isDelivering={deliveringId === coupon._id}
              onToggleSelect={onToggleSelect}
              onOpenDeliverModal={onOpenDeliverModal}
              onOpenHistoryModal={onOpenHistoryModal}
              onOpenShareHistoryModal={onOpenShareHistoryModal}
              onOpenSignatureModal={onOpenSignatureModal}
              onOpenVerificationModal={onOpenVerificationModal}
              onUpdateRecipientPhone={onUpdateRecipientPhone}
              copyToClipboard={copyToClipboard}
              allowOverIssue={allowOverIssue}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- Sub-components ---

const OrderSummaryBadge: FC<{
  summary: OrderCouponSummary;
  sequence?: number;
}> = ({ summary, sequence }) => {
  const { purchasedCount, deliveredCount } = summary;
  const isOver = sequence != null && sequence > purchasedCount;

  return (
    <div className="flex flex-col items-start gap-0.5">
      <span
        className={`inline-flex items-center gap-0.5 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-bold ${
          isOver
            ? 'animate-pulse bg-red-600 text-white ring-2 ring-red-700'
            : 'bg-slate-100 text-slate-700'
        }`}
        title={`${
          sequence ?? '?'
        }번째 요청 / 구매 ${purchasedCount}개 중 발송완료 ${deliveredCount}개${
          isOver ? '\n구매 수량 초과! 발송 금지!' : ''
        }`}
      >
        {sequence ?? '?'}/{purchasedCount}
        <span
          className={`text-[10px] font-medium ${
            isOver ? 'text-white' : 'text-green-600'
          }`}
        >
          (발송{deliveredCount})
        </span>
      </span>
      {isOver && (
        <span
          className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-red-700 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white ring-2 ring-red-800"
          title="구매 수량보다 많이 신청된 위험 쿠폰입니다. 발송하지 마세요!"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          초과발송
        </span>
      )}
    </div>
  );
};

const CopyPhonesButton: FC<{
  filteredData: GiftCouponForRedemption[];
  copyToClipboard: (text: string) => void;
  showToast: (msg: string) => void;
}> = ({ filteredData, copyToClipboard, showToast }) => (
  <button
    onClick={() => {
      const phones = filteredData
        .map((c) => c.recipient?.phoneNumber)
        .filter((p): p is string => !!p)
        .map((p) => p.replace(/[^0-9]/g, ''))
        .filter((p) => p.length > 0);
      if (phones.length === 0) {
        showToast('복사할 번호가 없습니다.');
        return;
      }
      copyToClipboard(phones.join('\n'));
    }}
    title={`필터된 수신자 번호 전체 복사 (${filteredData.length}건)`}
    className="inline-flex items-center gap-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-indigo-700 hover:bg-indigo-200"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-3 w-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
    번호 복사 ({filteredData.length})
  </button>
);

const CouponRow: FC<{
  coupon: GiftCouponForRedemption;
  isSelected: boolean;
  isDelivering: boolean;
  onToggleSelect: (couponId: string) => void;
  onOpenDeliverModal: (couponId: string) => void;
  onOpenHistoryModal: (coupon: GiftCouponForRedemption) => void;
  onOpenShareHistoryModal: (coupon: GiftCouponForRedemption) => void;
  onOpenSignatureModal: (signatureImage: Image) => void;
  onOpenVerificationModal: (verification: VerificationData) => void;
  onUpdateRecipientPhone: (couponId: string, newPhone: string) => Promise<boolean>;
  copyToClipboard: (text: string) => void;
  allowOverIssue?: boolean;
}> = ({
  coupon,
  isSelected,
  isDelivering,
  onToggleSelect,
  onOpenDeliverModal,
  onOpenHistoryModal,
  onOpenShareHistoryModal,
  onOpenSignatureModal,
  onOpenVerificationModal,
  onUpdateRecipientPhone,
  copyToClipboard,
  allowOverIssue,
}) => {
  const choiceUnitName = getChoiceUnitName(coupon);
  const priceInfo = getPriceInfo(coupon);
  const ordererInfo = getOrdererInfo(coupon);
  const isDuplicate = (coupon as GiftCouponForRedemptionDashboard).isDuplicatePhone;
  const isAdditionalPhoneConflict = (coupon as GiftCouponForRedemptionDashboard)
    .isAdditionalPhoneConflict;
  const orderSummary = (coupon as GiftCouponForRedemptionDashboard).orderCouponSummary;
  const redeemSequence = (coupon as GiftCouponForRedemptionDashboard).redeemSequence;
  const isOver = isOverIssued(coupon);

  return (
    <tr
      className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''} ${
        isOver
          ? 'border-l-4 border-l-red-600 bg-red-50/70'
          : isAdditionalPhoneConflict
          ? 'border-l-4 border-l-orange-500 bg-orange-50/60'
          : isDuplicate
          ? 'border-l-4 border-l-red-400'
          : ''
      } ${coupon.isAdditional ? 'bg-amber-50/40' : ''}`}
    >
      {/* Checkbox */}
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(coupon._id)}
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </td>

      {/* 쿠폰번호 / _id */}
      <td className="whitespace-nowrap px-3 py-2">
        <div className="flex items-center gap-1.5">
          {coupon.isAdditional && (
            <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
              재발행
            </span>
          )}
          {coupon.shareHistory && coupon.shareHistory.length > 0 && (
            <button
              onClick={() => onOpenShareHistoryModal(coupon)}
              className="inline-flex items-center rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700 hover:bg-purple-200"
              title="공유 이력 보기"
            >
              {coupon.shareHistory.length}
            </button>
          )}
          <span className="font-mono text-sm text-gray-700">
            {coupon.fullCode || '-'}
          </span>
        </div>
        <button
          onClick={() => copyToClipboard(coupon._id)}
          className="mt-0.5 font-mono text-[10px] text-gray-400 hover:text-indigo-600"
          title="클릭하여 _id 복사"
        >
          {coupon._id.slice(-6)}
        </button>
      </td>

      {/* 현재 발급상황 */}
      <td className="whitespace-nowrap px-3 py-2">
        {orderSummary ? (
          <OrderSummaryBadge summary={orderSummary} sequence={redeemSequence} />
        ) : (
          <span className="text-[10px] text-gray-300">-</span>
        )}
      </td>

      {/* 상품 */}
      <td className="px-3 py-2">
        <div className="flex items-center gap-3">
          {getImageUrl(coupon) && (
            <img
              src={getImageUrl(coupon)}
              alt=""
              className="h-10 w-10 rounded object-cover"
            />
          )}
          <div>
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium text-gray-900">{getItemName(coupon)}</p>
              {choiceUnitName && (
                <ChoiceUnitLink
                  coupon={coupon}
                  name={choiceUnitName}
                  copyToClipboard={copyToClipboard}
                />
              )}
            </div>
            {choiceUnitName && (
              <p className="max-w-[200px] text-xs text-indigo-600">{choiceUnitName}</p>
            )}
          </div>
        </div>
      </td>

      {/* 정가 */}
      <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-500">
        {priceInfo.priceRegular.toLocaleString()}원
      </td>

      {/* 판매가 / 도매가 */}
      <td className="whitespace-nowrap px-3 py-2 text-right">
        <div className="text-sm font-medium text-gray-900">
          {priceInfo.priceRetail.toLocaleString()}원
        </div>
        <div className="text-sm text-indigo-600">
          {priceInfo.priceWhole.toLocaleString()}원
        </div>
      </td>

      {/* 주문자 */}
      <td className="whitespace-nowrap px-3 py-2">
        <p className="text-sm font-medium text-gray-900">{ordererInfo.name}</p>
      </td>

      {/* 구매일 */}
      <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
        {formatDate(ordererInfo.purchasedAt)}
      </td>

      {/* 메모 */}
      <td className="max-w-[150px] px-3 py-2">
        {coupon.memo ? (
          <p className="truncate text-xs text-gray-600" title={coupon.memo}>
            {coupon.memo}
          </p>
        ) : (
          <span className="text-[10px] text-gray-300">-</span>
        )}
      </td>

      {/* 수신자 정보 */}
      <td className="whitespace-nowrap px-3 py-2">
        <RecipientPhoneCell
          coupon={coupon}
          isDuplicate={isDuplicate}
          isAdditionalPhoneConflict={isAdditionalPhoneConflict}
          onOpenHistoryModal={onOpenHistoryModal}
          onOpenSignatureModal={onOpenSignatureModal}
          onOpenVerificationModal={onOpenVerificationModal}
          onUpdateRecipientPhone={onUpdateRecipientPhone}
          copyToClipboard={copyToClipboard}
        />
      </td>

      {/* 신청일시 / 만료일 */}
      <td className="whitespace-nowrap px-3 py-2">
        <div className="text-sm text-gray-700">
          {formatDateTime(coupon.recipient?.requestedAt)}
        </div>
        <div className="text-[11px] text-gray-400">{formatDate(coupon.expiresAt)}</div>
      </td>

      {/* 액션 */}
      <td className="whitespace-nowrap px-3 py-2">
        <button
          onClick={() => onOpenDeliverModal(coupon._id)}
          disabled={isDelivering || (isOver && !allowOverIssue)}
          title={
            isOver
              ? allowOverIssue
                ? '구매 수량 초과 — 강제 발송 모드로 발송합니다'
                : '구매 수량 초과! 발송 불가'
              : undefined
          }
          className={`rounded px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
            isOver
              ? allowOverIssue
                ? 'bg-red-600 hover:bg-red-700 ring-2 ring-red-700'
                : 'bg-red-300 hover:bg-red-300'
              : 'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {isOver
            ? allowOverIssue
              ? isDelivering
                ? '처리중...'
                : '강제 발송'
              : '발송 불가'
            : isDelivering
            ? '처리중...'
            : '발송처리'}
        </button>
      </td>
    </tr>
  );
};

const ChoiceUnitLink: FC<{
  coupon: GiftCouponForRedemption;
  name: string;
  copyToClipboard: (text: string) => void;
}> = ({ coupon, name, copyToClipboard }) => {
  const hasWholeUrl =
    coupon.selectedChoiceUnit &&
    typeof coupon.selectedChoiceUnit === 'object' &&
    coupon.selectedChoiceUnit.wholeUrl;

  if (hasWholeUrl && typeof coupon.selectedChoiceUnit === 'object') {
    return (
      <a
        href={coupon.selectedChoiceUnit.wholeUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`도매사이트 이동: ${name}`}
        className="shrink-0 rounded p-0.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
      >
        <ExternalLinkIcon />
      </a>
    );
  }

  return (
    <button
      onClick={() => copyToClipboard(name)}
      title={name}
      className="shrink-0 rounded p-0.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
    >
      <CopyIcon />
    </button>
  );
};

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3.5 w-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3.5 w-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

const PencilIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3 w-3"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const RecipientPhoneCell: FC<{
  coupon: GiftCouponForRedemption;
  isDuplicate: boolean | undefined;
  isAdditionalPhoneConflict: boolean | undefined;
  onOpenHistoryModal: (coupon: GiftCouponForRedemption) => void;
  onOpenSignatureModal: (signatureImage: Image) => void;
  onOpenVerificationModal: (verification: VerificationData) => void;
  onUpdateRecipientPhone: (couponId: string, newPhone: string) => Promise<boolean>;
  copyToClipboard: (text: string) => void;
}> = ({
  coupon,
  isDuplicate,
  isAdditionalPhoneConflict,
  onOpenHistoryModal,
  onOpenSignatureModal,
  onOpenVerificationModal,
  onUpdateRecipientPhone,
  copyToClipboard,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback(() => {
    const raw = coupon.recipient?.phoneNumber?.replace(/[^0-9]/g, '') || '';
    setEditValue(raw);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [coupon.recipient?.phoneNumber]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
  }, []);

  const confirmEdit = useCallback(async () => {
    const original = coupon.recipient?.phoneNumber?.replace(/[^0-9]/g, '') || '';
    if (editValue === original) {
      cancelEdit();
      return;
    }
    setIsSaving(true);
    const success = await onUpdateRecipientPhone(coupon._id, editValue);
    setIsSaving(false);
    if (success) {
      setIsEditing(false);
    }
  }, [
    coupon._id,
    coupon.recipient?.phoneNumber,
    editValue,
    onUpdateRecipientPhone,
    cancelEdit,
  ]);

  if (isEditing) {
    return (
      <div>
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="tel"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            disabled={isSaving}
            className="w-[110px] rounded border border-indigo-300 px-1.5 py-0.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            maxLength={11}
            placeholder="01012345678"
          />
          <button
            onClick={confirmEdit}
            disabled={isSaving}
            className="rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? '...' : '저장'}
          </button>
          <button
            onClick={cancelEdit}
            disabled={isSaving}
            className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-300 disabled:opacity-50"
          >
            취소
          </button>
        </div>
        {coupon.recipient?.name && (
          <p className="text-xs text-gray-500">{coupon.recipient.name}</p>
        )}
      </div>
    );
  }

  const recipientPhoneRaw = coupon.recipient?.phoneNumber?.replace(/[^0-9]/g, '') || '';
  const presetPhoneRaw = coupon.additionalRecipientPhone?.replace(/[^0-9]/g, '') || '';
  const isPhoneMatch =
    coupon.isAdditional && presetPhoneRaw && recipientPhoneRaw
      ? recipientPhoneRaw === presetPhoneRaw
      : null;

  return (
    <div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => {
            const phone = coupon.recipient?.phoneNumber;
            if (phone) copyToClipboard(phone);
          }}
          className="text-sm font-medium text-gray-900 hover:text-indigo-600"
          title="클릭하여 복사"
        >
          {formatPhone(coupon.recipient?.phoneNumber)}
        </button>
        <button
          onClick={startEdit}
          className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
          title="수신자 번호 수정"
        >
          <PencilIcon />
        </button>
        {coupon.verification &&
          (() => {
            const verifiedPhone = coupon.verification.phoneNumber.replace(/[^0-9]/g, '');
            const recipientPhone =
              coupon.recipient?.phoneNumber?.replace(/[^0-9]/g, '') || '';
            const isPhoneMismatch =
              verifiedPhone && recipientPhone && verifiedPhone !== recipientPhone;

            return (
              <button
                onClick={() => {
                  if (coupon.verification) onOpenVerificationModal(coupon.verification);
                }}
                className={`inline-flex items-center rounded-full p-1 ${
                  isPhoneMismatch
                    ? 'bg-red-100 text-red-700 ring-1 ring-red-300 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
                title={
                  isPhoneMismatch
                    ? `인증 번호 불일치! 인증: ${verifiedPhone} ≠ 수신자: ${recipientPhone}`
                    : `본인 인증 완료 (${new Date(
                        coupon.verification.verifiedAt,
                      ).toLocaleString('ko-KR')})`
                }
              >
                {isPhoneMismatch ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0010 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })()}
        {coupon.recipient?.signatureImage && (
          <button
            onClick={() => {
              const sig = coupon.recipient?.signatureImage;
              if (sig) onOpenSignatureModal(sig);
            }}
            className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-200"
            title="클릭하여 서명 확인"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            서명
          </button>
        )}
        {isAdditionalPhoneConflict && (
          <span
            className="inline-flex items-center gap-0.5 rounded-full bg-orange-200 px-2 py-0.5 text-[10px] font-bold text-orange-900 ring-1 ring-orange-400"
            title="이 번호로 같은 주문의 재발행 쿠폰이 전달되었습니다. 발송하지 마세요!"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            재발행 수령자
          </span>
        )}
        {isDuplicate && (
          <button
            onClick={() => onOpenHistoryModal(coupon)}
            className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 hover:bg-red-200"
            title="클릭하여 과거 이력 확인"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            중복
          </button>
        )}
        {isPhoneMatch === true && (
          <span
            className="inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700"
            title={`사전 등록 번호 일치: ${formatPhone(coupon.additionalRecipientPhone)}`}
          >
            번호일치
          </span>
        )}
        {isPhoneMatch === false && (
          <span
            className="inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700"
            title={`사전 등록 번호: ${formatPhone(
              coupon.additionalRecipientPhone,
            )} / 신청 번호: ${formatPhone(coupon.recipient?.phoneNumber)}`}
          >
            번호불일치
          </span>
        )}
      </div>
      {coupon.recipient?.name && (
        <p className="text-xs text-gray-500">{coupon.recipient.name}</p>
      )}
      {coupon.additionalRecipientPhone && (
        <p className="text-[10px] text-amber-600">
          확인번호: {formatPhone(coupon.additionalRecipientPhone)}
        </p>
      )}
      {isAdditionalPhoneConflict && (
        <p className="mt-0.5 text-[10px] font-bold text-orange-700">
          재발행 쿠폰 수령자와 동일 번호 - 발송 주의!
        </p>
      )}
    </div>
  );
};
